import type { ProductSelectOption } from '@/components/header/ProductSelect.types'
import { useAppState } from '@/composables/useAppState'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { useUserPrivileges } from '@/composables/useUserPrivileges'
import ProductConfigurationHelper from '@/lib/product-config/ProductConfigurationHelper'
import MachineHealthMonitoringConfiguration from '@/lib/product-config/products/MachineHealthMonitoringConfiguration'
import SteamTrapMonitoringConfiguration from '@/lib/product-config/products/SteamTrapMonitoringConfiguration'
import { Product, RoutedMenuItem } from '@/types'
import { asyncComputed } from '@vueuse/core'
import { computed } from 'vue'

export const productOptionFor: Record<Product, ProductSelectOption | undefined> = {
  [Product.Steam]: {
    key: SteamTrapMonitoringConfiguration.info.key,
    label: SteamTrapMonitoringConfiguration.info.shortTitle,
    'data-testid': 'steam'
  },
  [Product.MachineHealth]: {
    key: MachineHealthMonitoringConfiguration.info.key,
    label: MachineHealthMonitoringConfiguration.info.shortTitle
  }
}

/**
 * Shared product-aware main-navigation plumbing used by both the top nav
 * (Navigation.vue) and the bottom mobile menu (App.vue's <MainMenu>). Keeps the
 * product options, selected product, privilege-filtered menu items, and product
 * selection handler defined in one place instead of duplicated across both.
 */
export function useMainNavigation() {
  const { isItAdmin, enabledProducts, hasMultipleProductsEnabled } = useUserPrivileges()
  const { user } = useCurrentUser()
  const { currentProduct, setCurrentProduct } = useAppState()

  const productOptions = computed<ProductSelectOption[]>(() =>
    enabledProducts.value
      .map((p) => productOptionFor[p])
      .filter((o): o is ProductSelectOption => !!o)
  )

  const selectedProduct = computed<ProductSelectOption | null>(
    () => productOptions.value.find((p) => p.key === currentProduct.value) ?? null
  )

  const productConfiguration = computed(() =>
    ProductConfigurationHelper.getProductConfiguration(
      currentProduct.value,
      user.value?.customer?.integrations
    )
  )

  const routedMenuItems = asyncComputed<RoutedMenuItem[]>(async () => {
    const items: RoutedMenuItem[] = []
    for (const route of productConfiguration.value.routedMenuItems) {
      if (!route?.meta?.privilegeFunction) {
        items.push(route)
      } else if (await route.meta.privilegeFunction()) {
        items.push(route)
      }
    }
    return items
  }, [])

  function onProductSelected(option: ProductSelectOption) {
    setCurrentProduct(option.key)
  }

  return {
    isItAdmin,
    hasMultipleProductsEnabled,
    currentProduct,
    productOptions,
    selectedProduct,
    productConfiguration,
    routedMenuItems,
    onProductSelected
  }
}
